import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { faSignalStream } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PortfolioPerformanceTransactionsTable } from './transactions-table';
import { PortfolioPerformanceTransactionsMetrics } from './transactions-metrics';
import { TransactionsContext } from '../../../../../providers/portfolio-performance/transactions';
import { TransactionDataType } from '../../../../../providers/portfolio-performance/transactions/transactions.types';
import { ReportFilterMultiSelect } from '../../../../../components/report-filter-buttons/report-filter-multi-select';
import Utils from '../../../../../utils/immersive_utils';
import './styles.scss';

export const TransactionsCard = () => {
  const { data = [], loading } = useContext(TransactionsContext);

  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [selectedInstTypes, setSelectedInstTypes] = useState<string[]>([]);
  // Tracks every event/instrument type ever seen so far, so a brand-new type
  // showing up on a LATER poll (e.g. the account's first 24h window had no
  // fee events, then one occurs) gets auto-included in the filter — not
  // silently hidden just because it wasn't around at the initial load.
  const knownEventTypesRef = useRef<Set<string>>(new Set());
  const knownInstTypesRef = useRef<Set<string>>(new Set());

  const eventTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    return data.reduce<{ value: string; label: string; isVisible: boolean }[]>((acc, tx) => {
      if (tx.event_type && !seen.has(tx.event_type)) {
        seen.add(tx.event_type);
        acc.push({
          value: tx.event_type,
          label: Utils.capitalizeFirstLetter(tx.event_type.replace(/_/g, ' ').toLowerCase()),
          isVisible: true,
        });
      }
      return acc;
    }, []);
  }, [data]);

  const instTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    return data.reduce<{ value: string; label: string; isVisible: boolean }[]>((acc, tx) => {
      if (tx.instrument_type && !seen.has(tx.instrument_type)) {
        seen.add(tx.instrument_type);
        acc.push({
          value: tx.instrument_type,
          label: Utils.capitalizeFirstLetter(tx.instrument_type) ?? tx.instrument_type,
          isVisible: true,
        });
      }
      return acc;
    }, []);
  }, [data]);

  useEffect(() => {
    const newEventTypes = eventTypeOptions
      .map((o) => o.value)
      .filter((v) => !knownEventTypesRef.current.has(v));
    if (newEventTypes.length > 0) {
      newEventTypes.forEach((v) => knownEventTypesRef.current.add(v));
      setSelectedEventTypes((prev) => [...prev, ...newEventTypes]);
    }

    const newInstTypes = instTypeOptions
      .map((o) => o.value)
      .filter((v) => !knownInstTypesRef.current.has(v));
    if (newInstTypes.length > 0) {
      newInstTypes.forEach((v) => knownInstTypesRef.current.add(v));
      setSelectedInstTypes((prev) => [...prev, ...newInstTypes]);
    }
  }, [eventTypeOptions, instTypeOptions]);

  const filteredData = useMemo<TransactionDataType[]>(() => {
    if (selectedEventTypes.length === 0 && selectedInstTypes.length === 0) return data;
    return data.filter(
      (tx) =>
        selectedEventTypes.includes(tx.event_type) &&
        selectedInstTypes.includes(tx.instrument_type),
    );
  }, [data, selectedEventTypes, selectedInstTypes]);

  const hasTransactions = data && data.length > 0;
  const lastUpdate = new Date().toISOString();
  const lastUpdateFormatted = lastUpdate
    ? moment.parseZone(lastUpdate).format('DD MMM YYYY - HH:mm:ss') + ' UTC'
    : '-';

  return (
    <div className="portfolio-performance-transactions-table-container">
      <div className="portfolio-performance-transactions-table-content">
        <div className="portfolio-performance-transactions-table-content-filters">
          <span>Last transactions (rolling 24h)</span>
          <div className="portfolio-performance-transactions-table-last-update">
            <FontAwesomeIcon icon={faSignalStream} className="live-signal-icon" />
            <span className="live-data-label">Live Data</span>
            {hasTransactions && <span className="live-data-timestamp">{lastUpdateFormatted}</span>}
          </div>
        </div>

        {hasTransactions && <PortfolioPerformanceTransactionsMetrics data={data} />}

        {(instTypeOptions.length > 1 || eventTypeOptions.length > 1) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            {instTypeOptions.length > 1 && (
              <ReportFilterMultiSelect
                titleFilter="Inst Type"
                dataArr={instTypeOptions}
                selected={selectedInstTypes}
                setSelected={setSelectedInstTypes}
                disableBorder
                className="portfolio-performance-transactions-filter"
                disableButtonRipple
              />
            )}
            {eventTypeOptions.length > 1 && (
              <ReportFilterMultiSelect
                titleFilter="Event Type"
                dataArr={eventTypeOptions}
                selected={selectedEventTypes}
                setSelected={setSelectedEventTypes}
                disableBorder
                className="portfolio-performance-transactions-filter"
                disableButtonRipple
              />
            )}
          </div>
        )}

        <PortfolioPerformanceTransactionsTable data={filteredData} loading={loading} />
      </div>
    </div>
  );
};
